/**
 * Starting and watching the vendor lookups.
 *
 * A lookup scrapes the vendor for a job's procedure, parts and labor. It is SLOW —
 * minutes, not seconds — and there is exactly ONE vendor seat for the whole shop,
 * so lookups queue and run one at a time. That is why this is a queue rather than
 * a request: nothing here returns an answer, it returns a job to watch.
 *
 * `apiRoot`, not `api`: the route is mounted at `/api/procedures`, and `api` points at
 * `/api/admin`.
 */

import { apiRoot } from '../apiInstances';

export type LookupStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'cancelling'
  /**
   * Finished, correct, and waiting on a person.
   *
   * The vendor published more than one operation for the job — each bank of a V6 and
   * the pair together — and nothing may choose between them, so the lookup stopped
   * and asked. The result is kept server-side, so answering costs a moment rather
   * than another run of the shop's only vendor seat.
   */
  | 'needs_choice';

export interface LookupOption {
  operation: string;
  /** The side, where the vendor qualifies by one. Null for R&I versus R&R. */
  qualifier: string | null;
  /** the vendor's own published time, so the choice is made against its figures. */
  hours: number;
}

export interface LookupProgress {
  /** Where the scrape is, in words. "reading Cooling System > Water Pump". */
  text?: string | null;
  /**
   * A real fraction, or absent.
   *
   * Absent until the total is genuinely known — there is deliberately no
   * timer-driven percentage, because a bar sitting at 90% for ten minutes is worse
   * than honest step text.
   */
  done?: number | null;
  total?: number | null;
  /** What `done`/`total` are counting, so the screen can label it. */
  unit?: string | null;
}

export interface LookupJob {
  jobId: string;
  status: LookupStatus;
  /**
   * Which document this job belongs to.
   *
   * Returned by the server and needed here: answering the vehicle question writes
   * against the DOCUMENT, never against a vehicle id the client supplies, so the
   * server decides which vehicle and which shop from something it can verify.
   */
  documentType?: 'estimate' | 'work_order' | null;
  documentId?: number | null;
  /** 0 means this job holds the vendor seat. */
  position: number;
  etaMinutes?: number | null;
  /** Measured from this shop's own recent lookups — an estimate, never a promise. */
  etaIsEstimate?: boolean;
  progress?: LookupProgress | null;
  /** What the advisor asked for, so a queue of jobs is readable. */
  label?: string | null;
  createdLineId?: number | null;
  supersededLineId?: number | null;
  /** Present while the job is waiting for someone to pick an operation. */
  needsChoice?: { options: LookupOption[]; message: string } | null;
  /**
   * Present while the job is waiting for someone to say WHICH COMPONENT they meant.
   *
   * A different question from `needsChoice`, and asked earlier: this one comes
   * before anything has been read in depth, because "wheel hub" is the front hub or
   * the rear axle bearing and those are different procedures with different parts.
   * Answering it starts a fresh lookup down the single chosen path.
   */
  needsComponentChoice?: {
    query: string;
    message: string;
    options: Array<{ path: string[]; component: string; reason: string }>;
  } | null;
  /**
   * Present when the lookup could not tell WHICH VEHICLE this is.
   *
   * Asked earliest of the three, before a single page is read: the vendor publishes
   * this truck as 2WD and as 4WD and they are different jobs — the 4WD front hub
   * takes a halfshaft nut the 2WD does not. Nothing on file settles it, and guessing
   * produces a confident quote from the wrong article.
   *
   * `options` is the vendor's OWN wording for the vehicles it publishes. Shown
   * verbatim; re-describing them would be inventing a list the advisor cannot check.
   */
  needsVehicleDetail?: {
    field: 'driveType' | 'trim' | 'engine';
    question: string;
    options: Array<{ ymm: string; engine: string }>;
  } | null;
  /**
   * The VIN and the vehicle record disagree.
   *
   * A sharper question than the one above: one of the two is wrong, and only someone
   * looking at the vehicle knows which. Never resolved in either direction on its own.
   */
  vehicleConflict?: {
    field: 'driveType' | 'trim' | 'engine' | 'year' | 'make';
    recorded: string | null;
    decoded: string | null;
    question: string;
  } | null;
  /**
   * What actually landed on the quote.
   *
   * "Done" and "$0 with no parts" are different facts. A job that succeeded and put
   * nothing billable on the estimate is the failure this whole flow is most likely to
   * produce quietly, so the panel can say the number without opening the estimate.
   */
  totalPrice?: number | null;
  billedPartCount?: number | null;
  /** Offered, never billed. Someone adds one by hand or nobody does. */
  suggestedPartCount?: number | null;
  /** References the scrape could not follow. Reported, never silently dropped. */
  unreadable?: string[];
  error?: string | null;

  /**
   * When it ran, and what it did while it ran.
   *
   * A lookup reads pages on a subscription with ONE seat, and its only trace used to
   * be a line on an estimate. That is not enough to check it: a price that looks
   * wrong needs to be followable back to the pages it came from, and a lookup that
   * took four minutes and one that took forty are different events even when they
   * produce the same line.
   */
  createdAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  durationSeconds?: number | null;
  /** Which path worked, of the candidates tried. */
  pathUsed?: string[] | null;
  /** Every candidate tried, with the reason each failed, when none worked. */
  attempts?: Array<{ path: string[]; error?: string }>;
  /** The pages the scrape actually opened — the trail you can go and check. */
  urlsVisited?: string[];
}

export interface StartLookupRequest {
  documentType: 'estimate' | 'work_order';
  documentId: number;
  /** Omit to create a NEW line rather than extend an existing one. */
  lineId?: number;
  /** Accepting a recommendation the quote already offered. */
  recommendationId?: string;
  /**
   * Where to look, most likely first.
   *
   * The vendor files the same component under different groups depending on the
   * vehicle, so a single path is brittle — the server tries these in order and
   * reports which one worked.
   */
  lookupPathCandidates?: string[][];
  /**
   * What the advisor TYPED, when they typed rather than picked a suggestion.
   *
   * Resolved live against this vehicle's own vendor tree. There is no list of jobs
   * to pick from that could be right for every vehicle — the tree differs by year,
   * make, model and engine — so free text is the primary way in, and the job asks
   * when the words match more than one component.
   */
  query?: string;
  label?: string;
  mode?: 'add' | 'replace';
  /**
   * Which published operation to quote, when the vendor qualifies the job by side.
   *
   * Nothing guesses this. The vendor publishes each bank of a V6 separately AND the
   * pair together, and the pair is not the sum — the engine only comes apart once.
   * Left unset, the lookup runs and then FAILS naming every operation it found, so
   * the advisor chooses from real options rather than from a dropdown written here.
   */
  bank?: 'Left Side' | 'Right Side' | 'Both Sides';
  /** The same choice where the vendor names operations instead of sides — R&I vs R&R. */
  operation?: string;
  /**
   * Guards against a double click.
   *
   * This job takes minutes behind a button, so it WILL be pressed twice. The same
   * key returns the same job instead of starting a second scrape — which would
   * also be the thing that breaks the single vendor seat.
   */
  idempotencyKey?: string;
}

export const repairDataLookupApi = {
  /** Queue a lookup. Returns immediately with a job to watch, never the result. */
  start: (body: StartLookupRequest) =>
    apiRoot.post<LookupJob>('/procedures/lookups', body).then((r) => r.data),

  /** The whole queue in one request — polling each job separately is a request storm. */
  list: () =>
    apiRoot.get<{ jobs: LookupJob[] }>('/procedures/lookups').then((r) => r.data.jobs),

  get: (jobId: string) =>
    apiRoot.get<LookupJob>(`/procedures/lookups/${jobId}`).then((r) => r.data),

  /**
   * Answer a `needs_choice` job — which operation to quote.
   *
   * Applied from the result the lookup already captured, so this is a build and a
   * write rather than another scrape. Fast, and it does not touch the vendor seat.
   */
  choose: (jobId: string, choice: { bank?: string | null; operation?: string | null }) =>
    apiRoot
      .post<LookupJob>(`/procedures/lookups/${jobId}/choose`, choice)
      .then((r) => r.data),

  /**
   * Cancel, whether it is waiting or already running.
   *
   * Two outcomes, and they mean different things to whoever pressed it:
   * `cancelled` — it was queued, nothing ran, nothing is lost.
   * `cancelling` — it was running; it stops shortly and the work already done is
   * discarded.
   */
  cancel: (jobId: string) =>
    apiRoot
      .delete<{ status: 'cancelled' | 'cancelling' }>(`/procedures/lookups/${jobId}`)
      .then((r) => r.data),

  /**
   * Answer the vehicle question, recording HOW it was answered.
   *
   * Unlike `choose`, this does not resume anything. The answer is written to the
   * vehicle with its source, and the advisor runs the lookup again — a refused
   * lookup restarts in well under a minute, and holding the shop's only vendor seat
   * open while somebody walks out to read a badge costs more than starting over.
   *
   * `source` is the part that matters later. A bare '4WD' on the vehicle cannot be
   * judged; "an advisor chose this, from these options, on this job" can — and that
   * is what makes a wrong value findable and correctable instead of permanent.
   *
   * A SKIP IS SENT, not withheld. Recording nothing would leave "never asked" and
   * "asked and did not know" identical, and the same question would come back on
   * every run.
   */
  recordVehicleDetail: (body: {
    documentType: 'estimate' | 'work_order';
    documentId: number;
    field: 'drive_type' | 'trim' | 'engine_size' | 'transmission_type' | 'fuel_type';
    value: string | null;
    source: 'advisor_selected' | 'advisor_skipped';
    options?: string[];
    jobId?: string;
  }) =>
    apiRoot
      .post<{
        vehicleId: number;
        field: string;
        value: string | null;
        source: string;
        nextStep: string;
      }>('/procedures/vehicle-detail', body)
      .then((r) => r.data),
};

export default repairDataLookupApi;
